import { Dispatch, useMemo, useState } from "react";

import type {
  StaticMapOverlaysOverlayBase,
  StaticMapOverlaysOverlayConfiguration,
} from "@/lib/api";
import {
  PickListItem,
  PickListModal,
} from "@/features/widget-forms/forms/weather-model-map/modals/pick-list-modal";
import {
  ConfigureStaticOverlayModal,
  StaticOverlayDetails,
} from "@/features/widget-forms/forms/weather-model-map/modals/configure-static-overlay-modal";
import { DEFAULT_STATIC_OVERLAY_COLOR } from "@/features/shared/widgets/model-map/static-overlay-color-presets";
import { useTranslation } from "@/lib/i18n";

interface AddStaticOverlayModalProps {
  staticOverlays: StaticMapOverlaysOverlayBase[];
  selectedStaticOverlayIds: string[];
  onAdd: Dispatch<StaticMapOverlaysOverlayConfiguration>;
}

const AddStaticOverlayModal = ({
  staticOverlays,
  selectedStaticOverlayIds,
  onAdd,
}: AddStaticOverlayModalProps) => {
  const { t } = useTranslation();
  const items = useMemo(
    () =>
      staticOverlays.map((o): PickListItem => ({
        id: o.id,
        title: o.name,
      })),
    [staticOverlays],
  );

  const [configurationVisible, setConfigurationVisible] = useState(false);
  const [overlayId, setOverlayId] = useState<string>();

  const onPick = (id: string) => {
    setConfigurationVisible(true);
    setOverlayId(id);
  };

  const overlay = staticOverlays.find((o) => o.id === overlayId);

  function onSubmitConfiguration(details: StaticOverlayDetails) {
    if (!overlayId) return;
    const color = (details.color || DEFAULT_STATIC_OVERLAY_COLOR).trim();
    onAdd({
      static_map_overlay_id: overlayId,
      label: details.label.trim(),
      color,
      enabled_by_default: true,
    });
    setConfigurationVisible(false);
  }

  return (
    <>
      <PickListModal
        title={t("admin.widgets.fields.model_map.static_overlays.add")}
        description={t("admin.widgets.fields.model_map.static_overlays.add_description")}
        addText={t("admin.widgets.fields.model_map.static_overlays.add")}
        emptyText={t("admin.widgets.fields.model_map.static_overlays.none_available")}
        items={items}
        selected={selectedStaticOverlayIds}
        onPick={onPick}
        checkedItemStatusLabel={{
          text: t("admin.widgets.fields.model_map.static_overlays.already_added"),
          icon: "check",
        }}
      />
      <ConfigureStaticOverlayModal
        key={overlayId ?? "configure-static-overlay"}
        overlay={overlay}
        open={configurationVisible}
        onSubmit={onSubmitConfiguration}
        onCancel={() => setConfigurationVisible(false)}
        initialValues={{
          label: overlay?.name ?? "",
          color: DEFAULT_STATIC_OVERLAY_COLOR,
        }}
      />
    </>
  );
};

export default AddStaticOverlayModal;
